import React from 'react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import LegalLayout from '../components/LegalLayout';

const DeleteAccount: React.FC = () => {
  return (
    <div>
      <SEO
        title="Delete Your Account"
        description="How to permanently delete your eTENANT account and associated data, what information is removed, and how long the deletion process takes."
        path="/delete-account"
        breadcrumb="Delete Your Account"
      />

      {/* Page Header */}
      <PageHero
        crumb="Delete Account"
        title={<>Delete Account</>}
        subtitle="Last Updated: June 23, 2026"
      />

      {/* Content */}
      <LegalLayout>

      <p className="mb-4 legal-v2-lead">
        You have the right to request deletion of your eTENANT account and all associated personal data at any time. Follow the steps below to submit your request.
      </p>

      {/* How to Request */}
      <h2 className="mb-4 mt-5">How to Request Account Deletion</h2>

      <div className="mb-5 legal-v2-steps">
        <ol className="list-unstyled m-0">

          <li className="d-flex gap-3 mb-4">
            <span className="legal-v2-step-num">1</span>
            <div>
              <p className="fw-semibold mb-1">In-App (Recommended)</p>
              <p className="mb-0">
                Open eTENANT → <strong>Profile</strong> → <strong>Edit Profile</strong> → scroll to <strong>Danger Zone</strong> → tap <strong>Delete Account</strong> → confirm. Deletion is immediate.
              </p>
            </div>
          </li>

          <li className="d-flex gap-3 mb-4">
            <span className="legal-v2-step-num">2</span>
            <div>
              <p className="fw-semibold mb-1">Send an email to our support team</p>
              <p className="mb-0">
                Email us at{' '}
                <a href="mailto:support@etenant.net" className="text-primary fw-medium">support@etenant.net</a>{' '}
                with the subject line: <strong>"Account Deletion Request"</strong>
              </p>
            </div>
          </li>

          <li className="d-flex gap-3 mb-0">
            <span className="legal-v2-step-num">3</span>
            <div>
              <p className="fw-semibold mb-1">Include your account details</p>
              <p className="mb-1">In your email, provide the following:</p>
              <ul>
                <li>The email address or Google account linked to your eTENANT account</li>
                <li>Confirmation that you want all data permanently deleted</li>
              </ul>
              <p className="mb-0 mt-2">
                We will verify your identity and confirm the deletion within <strong>30 business days</strong>. You will receive a confirmation email once your account has been deleted.
              </p>
            </div>
          </li>

        </ol>
      </div>

      {/* What Gets Deleted */}
      <h2 className="mb-3 mt-5">What Data Gets Deleted</h2>
      <p className="mb-3">
        Upon successful deletion of your account, the following data will be permanently removed:
      </p>
      <ul className="mb-5">
        <li>Your profile information (name, email address, profile photo, nationality)</li>
        <li>All properties, units, photos, and amenity details</li>
        <li>All tenant profiles, emergency contacts, and communication preferences</li>
        <li>All rental agreements, lease terms, and checklists</li>
        <li>All payment records and transaction history</li>
        <li>All maintenance requests and resolution notes</li>
        <li>All property inventory records</li>
        <li>All AI agreement drafts</li>
        <li>All public listing data</li>
        <li>Push notification tokens and notification history</li>
        <li>Subscription and billing history</li>
        <li>Google Drive document metadata (references stored in our database)</li>
      </ul>

      {/* Retention Table */}
      <h2 className="mb-3 mt-5">Data Retention After Deletion</h2>
      <p className="mb-3">
        Some data may be retained for a limited period as required by law or for security compliance:
      </p>
      <div className="table-responsive mb-5">
        <table className="table legal-v2-table">
          <thead>
            <tr>
              <th>Data Type</th>
              <th>Retention Period</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Personal profile data</td>
              <td>Removed immediately (in-app) or within 30 days (email request)</td>
            </tr>
            <tr>
              <td>Properties, tenants, agreements, payments</td>
              <td>Removed within 30 days</td>
            </tr>
            <tr>
              <td>Audit and activity logs</td>
              <td>Retained for up to 1 year (security compliance)</td>
            </tr>
            <tr>
              <td>Billing records</td>
              <td>May be retained longer as required by tax or legal obligations</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Google Drive Note */}
      <div className="mb-5 legal-v2-callout legal-v2-callout-warning">
        <p className="fw-semibold mb-1">Important: Google Drive Documents</p>
        <p className="mb-0">
          If you use Google Drive document storage, your files are stored in your personal Google Drive — not on eTENANT servers. Deleting your eTENANT account will remove the metadata references from our database, but the actual files in your Google Drive will remain. You must manually delete them from Google Drive if you wish to remove them.
        </p>
      </div>

      {/* Cancel Subscription */}
      <h2 className="mb-3 mt-5">Cancel Your Subscription Before Deleting</h2>
      <p className="mb-3">
        If you have an active paid subscription, please cancel it through Google Play before requesting account deletion to avoid future charges:
      </p>
      <ol className="mb-5">
        <li>Open the <strong>Google Play Store</strong> on your Android device</li>
        <li>Tap your profile icon → <strong>Payments &amp; subscriptions</strong> → <strong>Subscriptions</strong></li>
        <li>Find <strong>eTENANT</strong> and tap <strong>Cancel subscription</strong></li>
      </ol>

      {/* Contact */}
      <h2 className="mb-3 mt-5">Contact Us</h2>
      <p className="mb-2">For account deletion requests or any questions about your data:</p>
      <p className="mb-1">
        Email: <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>
      </p>
      <p className="mb-1">Company: INVOVANE COMPANY</p>
      <p className="mb-0">Address: Manila, Philippines</p>

      </LegalLayout>
    </div>
  );
};

export default DeleteAccount;
